import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Typography, CircularProgress, Box,
    Snackbar, Alert, Button, Collapse, List, ListItem, ListItemIcon, ListItemText
} from '@mui/material';
import { Warning as WarningIcon, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { getAccountStatus, createAccountLink } from '../services/api';

const requirementDescriptions = {
    'business_profile.mcc': 'Select a business category (MCC)',
    'business_profile.url': 'Add your business website or online presence',
    'external_account': 'Link a bank account to receive payouts',
    'individual.address.city': 'Enter your city',
    'individual.address.line1': 'Enter your street address',
    'individual.address.postal_code': 'Enter your postal/ZIP code',
    'individual.address.state': 'Enter your state or region',
    'individual.dob.day': 'Enter your date of birth (day)',
    'individual.dob.month': 'Enter your date of birth (month)',
    'individual.dob.year': 'Enter your date of birth (year)',
    'individual.email': 'Enter your email address',
    'individual.first_name': 'Enter your first name',
    'individual.last_name': 'Enter your last name',
    'individual.phone': 'Enter your phone number',
    'individual.ssn_last_4': 'Provide the last 4 digits of your SSN',
    'settings.payments.statement_descriptor': 'Customize the statement name shown on customer bank statements',
    'tos_acceptance.date': 'Accept Stripe’s Terms of Service',
    'tos_acceptance.ip': 'Accept Stripe’s Terms of Service from a valid IP',
};

function StripeReturn() {
    const navigate = useNavigate();
    const { currentUser, loading: authLoading } = useAuth();
    const [loading, setLoading] = useState(true);
    const [missingRequirements, setMissingRequirements] = useState([]);
    const [showDetails, setShowDetails] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        const fetchStripeStatus = async () => {
            try {
                if (currentUser?.stripe_connected_account_id) {
                    const statusResponse = await getAccountStatus();
                    const data = statusResponse.data || {};
                    const currentlyDue = JSON.parse(data.requirements_currently_due?.replace(/'/g, '"') || '[]');
                    setMissingRequirements(currentlyDue);
                    if (currentlyDue.length === 0) {
                        setSnackbar({ open: true, message: 'Your Stripe account is fully set up!', severity: 'success' });
                    }
                }
            } catch (error) {
                console.error('Error fetching Stripe status:', error);
                setSnackbar({ open: true, message: 'Failed to verify Stripe status.', severity: 'error' });
            } finally {
                setLoading(false);
            }
        };

        if (!authLoading) fetchStripeStatus();
    }, [authLoading, currentUser]);

    const handleFinishOnboarding = async () => {
        try {
            const response = await createAccountLink();
            window.location.href = response.data.url;
        } catch (error) {
            setSnackbar({ open: true, message: 'Could not continue onboarding.', severity: 'error' });
        }
    };

    if (loading || authLoading) {
        return (
            <Container maxWidth="sm">
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    const onboardingComplete = missingRequirements.length === 0;

    return (
        <Container maxWidth="sm">
            <Box textAlign="center" mt={6}>
                <Typography variant="h5" gutterBottom>
                    {onboardingComplete ? 'Stripe Setup Complete 🎉' : 'Just One More Step 🚀'}
                </Typography>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                    {onboardingComplete
                        ? 'You’ve successfully linked your Stripe account. You’re ready to lend or receive payments.'
                        : 'Looks like you didn’t finish setting up your Stripe account. Let’s complete it now so you can start receiving payments.'}
                </Typography>

                {!onboardingComplete && (
                    <>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleFinishOnboarding}
                            sx={{ mt: 3 }}
                        >
                            Complete Stripe Setup
                        </Button>

                        <Box mt={2}>
                            <Button
                                variant="text"
                                color="secondary"
                                onClick={() => setShowDetails(!showDetails)}
                                endIcon={showDetails ? <ExpandLess /> : <ExpandMore />}
                            >
                                {showDetails ? 'Hide Details' : 'See What’s Missing'}
                            </Button>
                            <Collapse in={showDetails}>
                                <List dense sx={{ textAlign: 'left', mt: 2 }}>
                                    {missingRequirements.map((req, idx) => (
                                        <ListItem key={idx}>
                                            <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                                            <ListItemText
                                                primary={requirementDescriptions[req] || req.replaceAll('.', ' ').toUpperCase()}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Collapse>
                        </Box>
                    </>
                )}

                {onboardingComplete && (
                    <Button variant="contained" color="primary" onClick={() => navigate('/profile')} sx={{ mt: 3 }}>
                        Go to Profile
                    </Button>
                )}
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default StripeReturn;
